/**
 * The agent's calls, beside the rest of `/v1` in api.ts: status, the two
 * voice routes, the three thread routes the history view reads, and the two
 * that keep a reply's rating.
 *
 * `POST /v1/agent/chat` is not a call this file makes. It is an AI SDK UI
 * message stream, so the phone hands `useChat` a transport that knows the url,
 * the headers and the body, and the SDK does the posting and the reading.
 * Decision 5 of docs/plans/ai-setup-and-agent.md.
 *
 * Two things the transport carries that the SDK's default does not:
 *
 *   the tenant header   only when a tenant is given. Setup mode is a person
 *                       with no business yet, and the server refuses a header
 *                       there for the same reason `POST /v1/businesses` does.
 *   the context body    which screen the agent was opened from, and on the
 *                       setup door the name and currency the person already
 *                       typed, so the first question is not one they answered.
 *
 * `expo/fetch` by name (through `@/lib/client-fetch`), as api.ts explains: it
 * is the one that streams.
 */
import type {
  AgentFeedback,
  AgentFeedbackRequest,
  AgentLanguageTag,
  AgentProposalOutcome,
  AgentProposalOutcomeRequest,
  AgentScreenCard,
  AgentThreadListResponse,
  AgentThreadRenameRequest,
  AgentThreadSummary,
  LiveOrigin,
  RecordNote,
  SetupTurnContext,
  SetupUnderstanding,
} from '@alluzo/shared';
import type { UIMessage } from 'ai';
import { DefaultChatTransport } from 'ai';

import { sendWindow } from '@/lib/agent-history';
import { fetch } from '@/lib/client-fetch';
import { apiBaseUrl, personHeaders, readOrThrow, TENANT_HEADER } from '@/lib/api';

/** GET /v1/agent/status. `configured` false means the door is not drawn. */
export type AgentStatus = {
  configured: boolean;
  providers: { google: boolean; openai: boolean };
};

/** What the setup door already knows, so the agent does not ask for it again. */
export type SetupDraft = { businessName?: string; currency?: string };

/**
 * Where the agent was opened from. The server builds its tools and its prompt
 * from this and from the caller's grants; the setup screen is the one that
 * takes no tenant. `tz` / `region` / `localeCurrency` are the device's place
 * in the world, so setup can guess a business's currency without asking.
 */
export type AgentContext =
  | {
      screen: 'setup';
      draft?: SetupDraft;
      tz?: string;
      region?: string;
      localeCurrency?: string;
      /** How the person is talking; the server seats the interviewer for it. */
      mode?: 'voice' | 'text';
      /** The language they chose for the agent. Absent means English. */
      language?: AgentLanguageTag;
      /**
       * The one send that builds. No interviewer runs: the server hands the
       * understanding the phone held to the builder and streams the build
       * back as the `build_setup` part (docs/plans/done/onboarding-two-phases.md,
       * decision 4). Both ride together or not at all.
       */
      build?: true;
      understanding?: SetupUnderstanding;
      /**
       * Setup mode only: the setup session this turn runs on. Present means
       * the turn runs on the harness, and the server reads the thread and the
       * draft from the session, not from the body. The phone never sets it.
       */
      readonly session?: SetupTurnContext;
    }
  | {
      screen: string;
      recordId?: string;
      /** The device's IANA zone, so "today" is the person's day and not the server's. */
      tz?: string;
      /** The cards pinned where the person is looking; see `agent-screen-cards.ts`. */
      cards?: readonly AgentScreenCard[];
      /**
       * Create-with-voice handoff: the note from the call. Only on the one
       * turn that follows Draw it up.
       */
      voice?: {
        origin: Extract<LiveOrigin, 'new_record' | 'new_list'>;
        note: RecordNote;
      };
    };

export const agentStatus = async (cookie: string): Promise<AgentStatus> =>
  readOrThrow(await fetch(`${apiBaseUrl}/v1/agent/status`, { headers: personHeaders(cookie) }));

/**
 * One short recording — setup's, or a question to the assistant's orb —
 * becomes the same text a person could have typed.
 *
 * The bytes travel as a typed array and not as the recording's `File`:
 * `expo/fetch` replaces `content-type` with a blob's own MIME type, and the
 * route parses one media type only, so a blob body is refused with a 415
 * before the handler runs.
 *
 * The agent's language rides in the query, since the body is the recording:
 * it is the transcriber's hint, so a Hindi answer is heard as Hindi.
 */
export const transcribeSetupVoice = async (
  cookie: string,
  audio: Uint8Array<ArrayBuffer>,
  language?: AgentLanguageTag,
): Promise<string> =>
  readOrThrow<{ text: string }>(
    await fetch(`${apiBaseUrl}/v1/agent/transcribe${language === undefined ? '' : `?language=${encodeURIComponent(language)}`}`, {
      method: 'POST',
      headers: { ...personHeaders(cookie), 'content-type': 'application/octet-stream' },
      body: audio,
    }),
  ).then((body) => body.text);

/**
 * The player's source for one spoken setup reply.
 *
 * A GET the native player can stream, not a POST that waits for a whole file.
 * Cookie only: `personHeaders` would ask for JSON and the player would then
 * refuse the mpeg.
 */
export const setupVoiceSource = (
  cookie: string,
  text: string,
  language?: AgentLanguageTag,
): { uri: string; headers: Record<string, string> } => {
  const query = new URLSearchParams({ text });
  if (language !== undefined) query.set('language', language);
  return {
    uri: `${apiBaseUrl}/v1/agent/speech?${query.toString()}`,
    headers: { accept: 'audio/mpeg', cookie },
  };
};

export type AgentTransportInput = {
  cookie: string;
  /** Absent or null in setup mode, where there is no business to name. */
  tenantId?: string | null;
  /**
   * What the person is looking at. A function is read on every send, so a
   * context that moves between turns — the cards pinned on the screen change
   * the moment one is confirmed — is fresh each time.
   */
  context: AgentContext | (() => AgentContext);
  /** More headers, read on every send: the web studio names its device and tab (setup step 9.3). The phone passes none. */
  headers?: () => Record<string, string>;
};

const tenantOrPersonHeaders = (cookie: string, tenantId: string | null): Record<string, string> =>
  tenantId === null ? { cookie } : { cookie, [TENANT_HEADER]: tenantId };

/**
 * The transport `useChat` posts through.
 *
 * The body is the SDK's (`id`, `messages`, `trigger`, `messageId`) with two
 * changes: the context rides along, and a long thread posts only its trailing
 * window (decision 7 of docs/plans/agent-thread-history.md). The `id` is the
 * thread; the server keeps the turn under it inside a business.
 *
 * The cast on `fetch` is the SDK's own, from its Expo guide: `expo/fetch` is
 * the streaming implementation, and its type is a hair narrower than the
 * global's.
 */
export const agentTransport = ({
  cookie,
  tenantId = null,
  context,
  headers,
}: AgentTransportInput): DefaultChatTransport<UIMessage> =>
  new DefaultChatTransport<UIMessage>({
    api: `${apiBaseUrl}/v1/agent/chat`,
    fetch: fetch as unknown as typeof globalThis.fetch,
    headers: headers === undefined ? tenantOrPersonHeaders(cookie, tenantId) : () => ({ ...headers(), ...tenantOrPersonHeaders(cookie, tenantId) }),
    prepareSendMessagesRequest: ({ id, messages, trigger, messageId }) => ({
      body: {
        id,
        trigger,
        messageId,
        messages: sendWindow(messages),
        context: typeof context === 'function' ? context() : context,
      },
    }),
  });

/* ------------------------------------------------------------ the threads */

/**
 * GET /v1/agent/threads — this person's conversations in this business,
 * newest first. `before` is the `nextBefore` the last page answered;
 * `search` keeps only the conversations whose title holds those words.
 */
export const listAgentThreads = async (
  cookie: string,
  tenantId: string,
  before: string | null = null,
  search: string | null = null,
): Promise<AgentThreadListResponse> => {
  const params = new URLSearchParams();
  if (before !== null) params.set('before', before);
  if (search !== null && search.trim() !== '') params.set('q', search.trim());
  const joined = params.toString();
  const query = joined === '' ? '' : `?${joined}`;
  return readOrThrow(
    await fetch(`${apiBaseUrl}/v1/agent/threads${query}`, {
      headers: { accept: 'application/json', ...tenantOrPersonHeaders(cookie, tenantId) },
    }),
  );
};

/**
 * One thread whole: the summary, the messages in order, what the person did
 * with each card, and how they rated each reply. `feedback` is optional
 * because a server older than the ratings sends none.
 */
export type AgentThreadRead = {
  thread: AgentThreadSummary;
  messages: UIMessage[];
  outcomes: AgentProposalOutcome[];
  feedback?: AgentFeedback[];
};

/** GET /v1/agent/threads/:id */
export const readAgentThread = async (cookie: string, tenantId: string, threadId: string): Promise<AgentThreadRead> =>
  readOrThrow<AgentThreadRead>(
    await fetch(`${apiBaseUrl}/v1/agent/threads/${threadId}`, {
      headers: { accept: 'application/json', ...tenantOrPersonHeaders(cookie, tenantId) },
    }),
  );

/** PATCH /v1/agent/threads/:id — the person's own title for a conversation. Answers the renamed thread. */
export const renameAgentThread = async (cookie: string, tenantId: string, threadId: string, title: string): Promise<AgentThreadSummary> => {
  const body: AgentThreadRenameRequest = { title };
  return readOrThrow<AgentThreadSummary>(
    await fetch(`${apiBaseUrl}/v1/agent/threads/${threadId}`, {
      method: 'PATCH',
      headers: { accept: 'application/json', ...tenantOrPersonHeaders(cookie, tenantId), 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
};

/** DELETE /v1/agent/threads/:id — a conversation gone, with its messages, card outcomes and ratings. */
export const deleteAgentThread = async (cookie: string, tenantId: string, threadId: string): Promise<void> => {
  const response = await fetch(`${apiBaseUrl}/v1/agent/threads/${threadId}`, {
    method: 'DELETE',
    headers: tenantOrPersonHeaders(cookie, tenantId),
  });
  if (!response.ok) await readOrThrow(response);
};

/**
 * PUT /v1/agent/threads/:id/outcomes/:toolCallId — what the person did with a
 * card, so a reopened thread draws the receipt and not a live Confirm.
 */
export const putAgentOutcome = async (
  cookie: string,
  tenantId: string,
  threadId: string,
  toolCallId: string,
  outcome: AgentProposalOutcomeRequest,
  signal?: AbortSignal,
): Promise<void> => {
  const response = await fetch(`${apiBaseUrl}/v1/agent/threads/${threadId}/outcomes/${encodeURIComponent(toolCallId)}`, {
    method: 'PUT',
    headers: { ...tenantOrPersonHeaders(cookie, tenantId), 'content-type': 'application/json' },
    body: JSON.stringify(outcome),
    ...(signal === undefined ? {} : { signal }),
  });
  if (!response.ok) await readOrThrow(response);
};

const feedbackUrl = (threadId: string, messageId: string): string =>
  `${apiBaseUrl}/v1/agent/threads/${threadId}/messages/${encodeURIComponent(messageId)}/feedback`;

/**
 * PUT /v1/agent/threads/:id/messages/:messageId/feedback — how the person
 * rated one of Coach's replies, so a reopened thread draws the choice.
 */
export const putAgentFeedback = async (
  cookie: string,
  tenantId: string,
  threadId: string,
  messageId: string,
  request: AgentFeedbackRequest,
): Promise<void> => {
  const response = await fetch(feedbackUrl(threadId, messageId), {
    method: 'PUT',
    headers: { ...tenantOrPersonHeaders(cookie, tenantId), 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) await readOrThrow(response);
};

/** DELETE /v1/agent/threads/:id/messages/:messageId/feedback — a rating taken back. */
export const clearAgentFeedback = async (cookie: string, tenantId: string, threadId: string, messageId: string): Promise<void> => {
  const response = await fetch(feedbackUrl(threadId, messageId), {
    method: 'DELETE',
    headers: tenantOrPersonHeaders(cookie, tenantId),
  });
  if (!response.ok) await readOrThrow(response);
};
